import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../utils/supabase'
import { toast } from 'react-hot-toast'
import { AUTH_CONFIG } from '../constants'
import { logAuthError } from '../utils/authErrorLogger'

interface RegistrationForm {
  email: string
  password: string
  confirmPassword: string
  friendlyName: string
  isWhatsAppMember: boolean
  whatsAppNumber?: string
}

interface UseRegistrationOptions {
  /** Path to redirect to after successful login (passed to login page) */
  redirectTo?: string
}

/**
 * Custom hook to handle user registration logic
 */
export const useRegistration = (options: UseRegistrationOptions = {}) => {
  const { redirectTo } = options
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  /**
   * Validates a UK mobile number format
   * Must start with +44 and be followed by 10 digits
   */
  const validateUKMobileNumber = (number: string): boolean => {
    const ukMobileRegex = /^\+44[0-9]{10}$/
    return ukMobileRegex.test(number)
  }

  /**
   * Check if friendly name is available
   * Allows duplicates only with test users
   */
  const checkFriendlyName = async (name: string) => {
    const { data, error } = await supabase
      .from('players')
      .select('friendly_name, is_test_user')
      .eq('friendly_name', name)
      
    if (error) return false
    if (!data || data.length === 0) return true
    return data.every(player => player.is_test_user === true)
  }

  /**
   * Handle the registration process
   */
  const handleRegistration = async (formData: RegistrationForm) => {
    const { email, password, confirmPassword, friendlyName, isWhatsAppMember, whatsAppNumber } = formData
    setIsLoading(true)

    try {
      if (password !== confirmPassword) {
        toast.error('Passwords do not match!')
        return
      }

      // Validate WhatsApp number if user is a group member
      if (isWhatsAppMember) {
        if (!whatsAppNumber) {
          toast.error('WhatsApp number is required for group members')
          return
        }
        if (!validateUKMobileNumber(whatsAppNumber)) {
          toast.error('Please enter a valid UK mobile number starting with +44')
          return
        }
      }

      // Validate friendly name
      const isNameAvailable = await checkFriendlyName(friendlyName)
      if (!isNameAvailable) {
        toast.error('This friendly name is already taken. Please choose another one.')
        return
      }

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}${AUTH_CONFIG.EMAIL_REDIRECT_PATH}`
        }
      })

      if (authError) throw authError

      // Handle case where signup succeeded but user object is missing
      if (!authData.user) {
        console.error('Signup succeeded but no user returned:', authData)
        toast.error('Registration issue - please try logging in or contact support.')
        return
      }

      // Existing confirmed account: Supabase returns an obfuscated user with
      // no identities (to prevent email enumeration) instead of an error
      if (authData.user.identities && authData.user.identities.length === 0) {
        toast.error('An account with this email already exists. Please log in instead.', {
          duration: 6000
        })
        setTimeout(() => navigate('/login', { state: { from: redirectTo } }), 1500)
        return
      }

      // Existing unconfirmed account: Supabase re-sends the confirmation email
      // and returns the original user - don't create a duplicate player profile
      const { data: existingProfile } = await supabase
        .from('players')
        .select('id')
        .eq('user_id', authData.user.id)
        .maybeSingle()

      if (existingProfile) {
        toast.success(
          "You've already registered - we've re-sent the confirmation email. Check your inbox and junk folder, then log in.",
          { duration: 8000 }
        )
        setTimeout(() => navigate('/login', { state: { from: redirectTo } }), 1500)
        return
      }

      // Create player profile
      const { error: profileError } = await supabase
        .from('players')
        .insert({
          user_id: authData.user.id,
          friendly_name: friendlyName,
          caps: 0,
          active_bonuses: 0,
          active_penalties: 0,
          whatsapp_group_member: isWhatsAppMember ? 'Yes' : null,
          whatsapp_mobile_number: isWhatsAppMember ? whatsAppNumber : null
        })

      if (profileError) {
        console.error('Profile Error:', profileError)
        if (profileError.code === '23505') {
          toast.error('This friendly name is already taken. Please choose another one.')
        } else {
          throw profileError
        }
        return
      }

      toast.success('Registration successful! Please check your email to verify your account.', {
        duration: 5000
      })
      // Small delay to ensure toast is visible before navigation
      // Pass redirectTo so login page can redirect back after verification
      setTimeout(() => navigate('/login', { state: { from: redirectTo } }), 1500)
    } catch (error: any) {
      console.error('Registration error:', error)

      // Log error to database for troubleshooting
      await logAuthError({
        error_type: 'signup',
        error_code: error.code ?? error.status?.toString(),
        error_message: error.message || 'Unknown registration error',
        user_email: email,
        metadata: { friendlyName }
      })

      if (error.code === 'weak_password') {
        // Replace Supabase's verbose policy message with a readable one
        const friendlyMessage = error.message?.toLowerCase().includes('known to be weak')
          ? 'This password is too common and easy to guess. Please choose something more unique.'
          : 'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character.'
        toast.error(friendlyMessage, { duration: 8000 })
      } else if (error.message?.toLowerCase().includes('security purposes')) {
        toast.error('Please wait a moment before trying again')
      } else if (error.message?.toLowerCase().includes('jwt expired')) {
        toast.error('Your session has expired. Please try again.')
        await supabase.auth.signOut()
        navigate('/login', { state: { from: redirectTo } })
      } else {
        toast.error(error.message || 'Registration failed')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    handleRegistration,
    isLoading,
    validateUKMobileNumber
  }
}
